import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { AppointmentSection } from './AppointmentSection';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, Eye, ArrowLeft, Share2, Clock, MessageCircle, Send, User } from 'lucide-react';
import { Toaster, toast } from './ui/sonner';
import ChatWidget from './ChatWidget';
import DOMPurify from 'dompurify';
import { BlogCtaBar } from './LeadMagnets';
import LeadMagnetCTA from './LeadMagnetCTA';
import { BlogSubscribeForm } from './BlogSubscribeForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogComments = ({ slug }) => {
  const [comments, setComments] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchComments = useCallback(() => {
    fetch(`${BACKEND_URL}/api/blogs/${slug}/comments`)
      .then(r => r.json())
      .then(data => { setComments(data.comments || []); setTotal(data.total || 0); })
      .catch(() => {});
  }, [slug]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !comment.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/blogs/${slug}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), comment: comment.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Your question has been posted!');
        setComment('');
        fetchComments();
      } else {
        toast.error(data.detail || 'Could not post your comment.');
      }
    } catch { toast.error('Something went wrong.'); }
    setSubmitting(false);
  };

  const timeAgo = (isoStr) => {
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(isoStr).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="mt-12 pt-8 border-t border-gray-200" data-testid="blog-comments-section">
      <h3 className="text-xl font-bold text-gray-900 mb-1">
        Questions & Discussion {total > 0 && <span className="text-base font-normal text-gray-500">({total})</span>}
      </h3>
      <p className="text-sm text-gray-500 mb-6">Have a question about this topic? Our CA team reads every comment.</p>

      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-5 mb-8 border border-gray-200" data-testid="comment-form">
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} required
            data-testid="comment-name-input"
            className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white" />
          <input type="email" placeholder="Email (kept private, for our CAs to respond)" value={email} onChange={e => setEmail(e.target.value)} required
            data-testid="comment-email-input"
            className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white" />
        </div>
        <textarea placeholder="Ask a question or share your thoughts..." value={comment} onChange={e => setComment(e.target.value)} required rows={3}
          data-testid="comment-text-input"
          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none bg-white mb-3" />
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400">Email is kept private and used only to respond to your query.</p>
          <Button type="submit" disabled={submitting} data-testid="comment-submit-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-5">
            <Send className="w-3.5 h-3.5 mr-1.5" /> {submitting ? 'Posting...' : 'Post Question'}
          </Button>
        </div>
      </form>

      {/* Comments List */}
      {comments.length > 0 && (
        <div className="space-y-4">
          {comments.map((c, i) => (
            <div key={c.id || c.name || `comment-${i}`} data-testid={`comment-item-${i}`}
              className="flex gap-3 p-4 rounded-lg bg-white border border-gray-100 hover:border-emerald-200 transition-colors">
              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                {(c.name || 'A')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm text-gray-900">{c.name}</span>
                  <span className="text-xs text-gray-400">{timeAgo(c.createdAt)}</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{c.comment}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAppointmentOpen, setIsAppointmentOpen] = useState(false);
  const articleEndRef = React.useRef(null);
  const startTime = React.useRef(Date.now());
  const completionTracked = React.useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchBlog();
    startTime.current = Date.now();
    completionTracked.current = false;
  }, [slug]);

  // Track read completion via Intersection Observer
  useEffect(() => {
    if (!blog || !articleEndRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !completionTracked.current) {
          completionTracked.current = true;
          const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);
          fetch(`${BACKEND_URL}/api/admin/blog-analytics/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              slug, event: 'read_complete',
              time_on_page: timeOnPage, scroll_depth: 100
            })
          }).catch(() => {});
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(articleEndRef.current);
    return () => observer.disconnect();
  }, [blog, slug]);

  // Track page view
  useEffect(() => {
    if (blog) {
      fetch(`${BACKEND_URL}/api/admin/blog-analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, event: 'view', time_on_page: 0, scroll_depth: 0 })
      }).catch(() => {});
    }
  }, [blog, slug]);

  const fetchBlog = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blogs/${slug}`);
      
      if (response.ok) {
        const data = await response.json();
        setBlog(data);
      } else {
        navigate('/blogs');
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      navigate('/blogs');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      finance: 'bg-blue-100 text-blue-700',
      tax: 'bg-purple-100 text-purple-700',
      compliance: 'bg-green-100 text-green-700',
      payroll: 'bg-orange-100 text-orange-700',
      business_tips: 'bg-pink-100 text-pink-700',
      case_study: 'bg-indigo-100 text-indigo-700',
      fdi: 'bg-emerald-100 text-emerald-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  useEffect(() => {
    if (blog) {
      document.title = `${blog.metaTitle || blog.title} | Virtual CFO Consultancy`;
    }
  }, [blog]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header onBookAppointment={() => setIsAppointmentOpen(true)} />
        <div className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
        <Footer onBookAppointment={() => setIsAppointmentOpen(true)} />
      </div>
    );
  }

  if (!blog) return null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blog.title,
    "description": blog.excerpt,
    "image": blog.featuredImage || `${window.location.origin}/og-image.jpg`,
    "author": {
      "@type": "Person",
      "name": blog.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "NSRM Consultants India Private Limited",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "datePublished": blog.publishedAt,
    "dateModified": blog.updatedAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${window.location.origin}/blog/${blog.slug}`
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <meta name="description" content={blog.metaDescription || blog.excerpt} />
      <meta name="keywords" content={blog.tags.join(', ')} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={blog.title} />
      <meta property="og:description" content={blog.excerpt} />
      <meta property="og:image" content={blog.featuredImage || `${window.location.origin}/og-image.jpg`} />
      <meta property="og:url" content={`${window.location.origin}/blog/${blog.slug}`} />
      <meta property="article:published_time" content={blog.publishedAt} />
      <meta property="article:author" content={blog.author} />
      <meta property="article:section" content={blog.category} />
      {blog.tags.map((tag, idx) => (
        <meta key={`tag-meta-${tag}`} property="article:tag" content={tag} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={blog.title} />
      <meta name="twitter:description" content={blog.excerpt} />
      <meta name="twitter:image" content={blog.featuredImage || `${window.location.origin}/twitter-image.jpg`} />
      <link rel="canonical" href={`${window.location.origin}/blog/${blog.slug}`} />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>

      <Header onBookAppointment={() => setIsAppointmentOpen(true)} />
      
      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-8 sm:pb-12 px-4 sm:px-6 bg-gradient-to-br from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/blogs')}
            className="mb-4 sm:mb-6 hover:bg-emerald-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Articles
          </Button>

          <Badge className={getCategoryColor(blog.category) + " mb-3 sm:mb-4"}>
            {blog.category.replace('_', ' ')}
          </Badge>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            {blog.title}
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-4 sm:mb-6">{blog.excerpt}</p>

          <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-gray-600 text-sm">
            <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-400 flex items-center justify-center text-white font-bold mr-2 sm:mr-3 text-sm">
                {blog.author.charAt(0)}
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">{blog.author}</p>
                <p className="text-xs sm:text-sm">Finance Expert</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1.5" />
              <span className="text-xs sm:text-sm">{formatDate(blog.publishedAt || blog.createdAt)}</span>
            </div>
            
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1.5" />
              <span className="text-xs sm:text-sm">{getReadingTime(blog.content)}</span>
            </div>
            
            <div className="flex items-center">
              <Eye className="w-4 h-4 mr-1.5" />
              <span className="text-xs sm:text-sm">{blog.views} views</span>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              className="sm:ml-auto"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {blog.featuredImage && (
        <section className="px-4 sm:px-6 pb-8 sm:pb-12">
          <div className="max-w-4xl mx-auto">
            <img
              src={blog.featuredImage}
              alt={blog.title}
              className="w-full h-48 sm:h-72 md:h-96 object-cover rounded-xl sm:rounded-2xl shadow-xl"
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="px-4 sm:px-6 pb-12 sm:pb-20">
        <div className="max-w-4xl mx-auto">
          <div 
            className="prose prose-sm sm:prose-lg max-w-none
              prose-headings:font-bold prose-headings:text-gray-900
              prose-h2:text-xl sm:prose-h2:text-3xl prose-h2:mt-8 sm:prose-h2:mt-12 prose-h2:mb-4 sm:prose-h2:mb-6
              prose-h3:text-lg sm:prose-h3:text-2xl prose-h3:mt-6 sm:prose-h3:mt-8 prose-h3:mb-3 sm:prose-h3:mb-4
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4 sm:prose-p:mb-6
              prose-a:text-emerald-600 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-gray-900 prose-strong:font-semibold
              prose-ul:my-4 sm:prose-ul:my-6 prose-li:mb-2
              prose-ol:my-4 sm:prose-ol:my-6
              prose-img:rounded-xl prose-img:shadow-lg"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blog.content) }}
          />

          {/* Lead Magnet CTA (dynamic, attached to blog) */}
          <LeadMagnetCTA blogSlug={slug} />

          {/* Blog CTA Bar */}
          <BlogCtaBar />

          {/* Read completion marker */}
          <div ref={articleEndRef} className="h-1" />

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags:</h3>
            <div className="flex flex-wrap gap-2">
              {blog.tags.map((tag, idx) => (
                <Badge key={`tag-${tag}`} variant="outline" className="text-sm">
                  #{tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Questions & Comments Section */}
          <BlogComments slug={slug} />

          {/* CTA */}
          <div className="mt-8 sm:mt-12 p-5 sm:p-8 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              Need Expert Financial Guidance?
            </h3>
            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
              Our team of Chartered Accountants can help you with all your finance, accounting, and compliance needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setIsAppointmentOpen(true)}
                size="lg"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Book Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="max-w-2xl mx-auto">
          <BlogSubscribeForm />
        </div>
      </section>

      <Footer onBookAppointment={() => setIsAppointmentOpen(true)} />
      <AppointmentSection 
        isOpen={isAppointmentOpen} 
        onClose={() => setIsAppointmentOpen(false)} 
      />
      <ChatWidget onBookAppointment={() => setIsAppointmentOpen(true)} />
      <Toaster />
    </div>
  );
};